/**
 * How wide the tier grid's columns are for the space the grid actually has.
 *
 * A dealer price grid that shows no price is the one thing it must never do, and
 * that is what shipped: 890px of fixed columns inside the 495px the content well
 * has at a 1024 viewport once the sidebar and the right rail take their share.
 * Fixed columns cannot be made to fit that AND leave a readable product name, so
 * the split is by what the screen is FOR. The three columns a price list is
 * about, the SKU that names the row, the quantity break and the price, are fixed
 * and fit at 1024 with room to spare. The product name comes last and takes
 * whatever is left, which is generous at 1280 and 1440 and scrolls at 1024.
 * Merchants work from SKUs (Wild West, merchant finding 14), so the identifier
 * leading and the description trailing is also the order they read in.
 */
export const TIER_GRID_ROW_MARKER_WIDTH = 48;
export const TIER_GRID_ROW_MENU_WIDTH = 44;

/** Left to right. The elastic column is last, so nothing fixed is ever clipped. */
export const TIER_COLUMN_ORDER = ["sku", "minQuantity", "amount", "product"] as const;

export type TierColumnId = (typeof TIER_COLUMN_ORDER)[number];

/** Never squeezed, never scrolled out of view. Sums to 400. */
export const TIER_FIXED_COLUMN_WIDTHS: Record<Exclude<TierColumnId, "product">, number> = {
  sku: 140,
  minQuantity: 120,
  // "Price each (USD)" is the widest header the column carries.
  amount: 140,
};

/** Below this a product name says nothing, so the grid scrolls instead. */
export const TIER_PRODUCT_MIN_WIDTH = 150;
/**
 * The longest product name on the stage ("ARMAT by Alba Racing Polaris RZR
 * Adjustable Clutch Weights") draws in 395px, so past 420 the extra is waste.
 */
export const TIER_PRODUCT_MAX_WIDTH = 420;

export const tierFixedColumnsWidth = () =>
  Object.values(TIER_FIXED_COLUMN_WIDTHS).reduce((total, width) => total + width, 0);

/** What is left of the grid's own width once everything fixed has been drawn. */
export const tierProductWidth = (gridWidth: number): number => {
  const spare =
    gridWidth - TIER_GRID_ROW_MARKER_WIDTH - TIER_GRID_ROW_MENU_WIDTH - tierFixedColumnsWidth();

  return Math.min(TIER_PRODUCT_MAX_WIDTH, Math.max(TIER_PRODUCT_MIN_WIDTH, Math.floor(spare)));
};
